import { useMemo, useState } from "react";
import { Users, CheckCircle2, Clock, WifiOff, Home, Globe, Mic, MapPin, BookOpen } from "lucide-react";
import { StatCard, StatusBadge, inputCls } from "./common";
import { ChartBox, VBar, HBar, Donut, ExpectedBars } from "./Charts";
import { fmtTime } from "../lib/api";

export function SummaryCards({ s }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard testid="stat-registrations" label="Registrations Received" value={s.total_registrations} icon={Users} />
      <StatCard testid="stat-verified" label="Verified Attendees" value={s.verified} icon={CheckCircle2} accent="#16A34A"
        hint={s.attendance_percent != null ? `${s.attendance_percent}% of ${s.expected} expected` : "Official synchronized count"} />
      <StatCard testid="stat-pending" label="Pending Verification" value={s.pending} icon={Clock} accent="#D97706" />
      <StatCard testid="stat-offline" label="Offline Awaiting Sync" value={s.offline_pending_known} icon={WifiOff} accent="#5C3A21" hint="Known, reported by devices — provisional" />
      <StatCard testid="stat-parish" label="Parish Participants" value={s.parish} icon={Home} accent="#2D3B2D" />
      <StatCard testid="stat-external" label="External Participants" value={s.external} icon={Globe} />
      <StatCard testid="stat-resource" label="Invited Resource Persons" value={s.resource_persons} icon={Mic} />
      <StatCard testid="stat-gkks" label="GKKs / Ministries" value={`${s.gkks_represented} / ${s.ministries_represented}`} icon={MapPin} accent="#2D3B2D" />
    </div>
  );
}

export function MeetingCharts({ s }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <ChartBox title="Attendance by Zone" testid="chart-zone"><VBar data={s.by_zone} /></ChartBox>
      <ChartBox title="Parish vs External" testid="chart-donut"><Donut parish={s.parish} external={s.external} /></ChartBox>
      <ChartBox title="Attendance by GKK" note="Top 15" testid="chart-gkk" height={340}><HBar data={s.by_gkk} /></ChartBox>
      <ChartBox title="Attendance by Ministry Represented" testid="chart-ministry" height={340}><HBar data={s.by_ministry} color="#C85A32" /></ChartBox>
      <ChartBox title="Attendance by Position" testid="chart-position"><VBar data={s.by_position} color="#5C3A21" /></ChartBox>
      <ChartBox title="Actual vs Expected" testid="chart-expected">
        <ExpectedBars data={s.expected ? [{ name: "This meeting", expected: s.expected, actual: s.expected_attended }] : []} />
      </ChartBox>
    </div>
  );
}

export function AttendanceTable({ rows, actions, showContacts, testid = "attendance-table" }) {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [cat, setCat] = useState("");
  const [zone, setZone] = useState("");
  const zones = useMemo(() => [...new Set(rows.map((r) => r.zone).filter(Boolean))].sort(), [rows]);
  const list = rows.filter((r) => (!q || `${r.name} ${r.gkk} ${r.ministry} ${r.position} ${r.organization} ${r.participant_code || ""}`.toLowerCase().includes(q.toLowerCase()))
    && (!status || r.status === status) && (!cat || r.category === cat) && (!zone || r.zone === zone));
  return (
    <div className="sfap-card overflow-hidden" data-testid={testid}>
      <div className="flex flex-wrap gap-2 p-4 border-b border-[#E5DEC9] bg-[#FBF8F2]">
        <input className={`${inputCls} h-10 max-w-xs`} placeholder="Search name, GKK, ministry…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="attendance-search" />
        <select className={`${inputCls} h-10 w-auto`} value={status} onChange={(e) => setStatus(e.target.value)} data-testid="attendance-filter-status">
          <option value="">All statuses</option>
          {["present", "late", "pending", "pre_registered", "excused", "absent", "disputed", "rejected"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
        </select>
        <select className={`${inputCls} h-10 w-auto`} value={cat} onChange={(e) => setCat(e.target.value)} data-testid="attendance-filter-category">
          <option value="">Parish & External</option><option>Parish</option><option>External</option>
        </select>
        <select className={`${inputCls} h-10 w-auto`} value={zone} onChange={(e) => setZone(e.target.value)} data-testid="attendance-filter-zone">
          <option value="">All zones</option>{zones.map((z) => <option key={z}>{z}</option>)}
        </select>
        <span className="ml-auto self-center text-sm text-stone-500" data-testid="attendance-count">{list.length} shown</span>
      </div>
      <div className="overflow-x-auto max-h-[620px]">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white shadow-[0_1px_0_#E5DEC9] text-left text-[11px] uppercase tracking-wider text-stone-500">
            <tr>
              {["#", "Participant", "Position", "Zone", "GKK", "Unit", "Ministry", "Category", "Check-in", "Status"].map((h) => <th key={h} className="px-3 py-3 font-semibold whitespace-nowrap">{h}</th>)}
              {showContacts && <th className="px-3 py-3">Contact</th>}
              {actions && <th className="px-3 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={r.id} className={`border-t border-[#F0EADC] hover:bg-[#FBF8F2] ${r.archived ? "opacity-50 line-through" : ""}`} data-testid={`attendance-row-${r.id}`}>
                <td className="px-3 py-2.5 text-stone-400 tabular-nums">{i + 1}</td>
                <td className="px-3 py-2.5">
                  <div className="font-semibold text-stone-900">{r.name}</div>
                  <div className="text-[11px] text-stone-500">{[r.participant_code, r.role_in_meeting !== "Ordinary Participant" && r.role_in_meeting, r.organization].filter(Boolean).join(" · ")}</div>
                </td>
                <td className="px-3 py-2.5">{r.position || "—"}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">{r.zone || "—"}</td>
                <td className="px-3 py-2.5 min-w-[160px]">{r.gkk || "—"}</td>
                <td className="px-3 py-2.5">{r.unit || "—"}</td>
                <td className="px-3 py-2.5 min-w-[140px]">{r.ministry || "—"}</td>
                <td className="px-3 py-2.5">{r.category}</td>
                <td className="px-3 py-2.5 whitespace-nowrap tabular-nums">
                  {fmtTime(r.checkin_at)}
                  {r.source === "offline" && <div className="text-[10px] text-[#5C3A21] font-semibold">OFFLINE · synced {fmtTime(r.synced_at)}</div>}
                </td>
                <td className="px-3 py-2.5"><StatusBadge status={r.status} testid={`row-status-${r.id}`} /></td>
                {showContacts && <td className="px-3 py-2.5 text-xs">{[r.mobile, r.email].filter(Boolean).join(" · ") || "—"}</td>}
                {actions && <td className="px-3 py-2.5 text-right whitespace-nowrap">{actions(r)}</td>}
              </tr>
            ))}
            {!list.length && <tr><td colSpan={12} className="px-3 py-12 text-center text-stone-400">No attendance records yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const UpdatedAt = ({ at }) => (
  <span className="inline-flex items-center gap-2 text-xs text-stone-500" data-testid="last-updated">
    <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse" /> Live · last updated {at ? new Date(at).toLocaleTimeString("en-PH", { timeZone: "Asia/Manila" }) : "—"}
  </span>
);

export const BreakdownList = ({ title, items, icon: Icon = BookOpen }) => (
  <div className="sfap-card p-5">
    <h3 className="font-serif font-bold text-[#2D3B2D] mb-3 flex items-center gap-2"><Icon className="h-4 w-4" />{title}</h3>
    {items.length ? items.map((x) => (
      <div key={x.name} className="flex justify-between py-1.5 border-b border-dashed border-[#EFE8D8] text-sm"><span>{x.name}</span><b className="tabular-nums">{x.count}</b></div>
    )) : <p className="text-sm text-stone-400">None</p>}
  </div>
);
